import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'sonner';
import {
  Lightning,
  Clock,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  HourglassMedium,
  CaretRight,
  Pulse,
  Warning,
  Funnel,
  ArrowLeft,
} from '@phosphor-icons/react';

/* ── Status config ── */
const STATUS_CONFIG = {
  queued: { label: 'Queued', color: '#7D8896', bg: 'rgba(107,118,133,0.08)', Icon: Clock },
  running: { label: 'Running', color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', Icon: Pulse },
  waiting: { label: 'Waiting', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', Icon: HourglassMedium },
  retrying: { label: 'Retrying', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', Icon: ArrowsClockwise },
  completed: { label: 'Completed', color: '#00E5C0', bg: 'rgba(0,229,192,0.06)', Icon: CheckCircle },
  failed: { label: 'Failed', color: '#EF4444', bg: 'rgba(239,68,68,0.06)', Icon: XCircle },
  cancelled: { label: 'Cancelled', color: '#7D8896', bg: 'rgba(107,118,133,0.06)', Icon: XCircle },
  partially_completed: { label: 'Partially done', color: '#F59E0B', bg: 'rgba(245,158,11,0.06)', Icon: Warning },
};

const FILTER_TABS = [
  { id: null, label: 'All' },
  { id: 'running', label: 'Active' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

function formatDuration(ms) {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return `${min}m ${remSec}s`;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.queued;
  const { Icon } = cfg;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border"
      style={{ color: cfg.color, background: cfg.bg, borderColor: `${cfg.color}20` }}
    >
      <Icon size={12} weight="bold" />
      {cfg.label}
    </span>
  );
}

/* ── Worker progress dots ── */
function WorkerProgress({ workerChain, currentIndex, status: runStatus }) {
  return (
    <div className="flex items-center gap-1" data-testid="worker-progress">
      {workerChain.map((w, i) => {
        let dotColor = '#2A2F38';
        if (i < currentIndex || (runStatus === 'completed' && i === workerChain.length - 1)) dotColor = '#00E5C0';
        else if (i === currentIndex && runStatus === 'running') dotColor = '#3B82F6';
        else if (i === currentIndex && runStatus === 'failed') dotColor = '#EF4444';
        else if (i === currentIndex && runStatus === 'waiting') dotColor = '#F59E0B';

        return (
          <React.Fragment key={i}>
            {i > 0 && <div className="w-3 h-px" style={{ background: i <= currentIndex ? '#00E5C030' : '#2A2F38' }} />}
            <div
              className="w-2 h-2 rounded-full transition-colors"
              style={{ background: dotColor }}
              title={w.worker_name || `Worker ${i + 1}`}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Run card ── */
function RunCard({ run, onClick }) {
  const chain = run.worker_chain || [];
  const workerNames = chain.map(w => w.worker_name).filter(Boolean);
  const summary = run.supervisor_summary || '';
  const isActive = ['running', 'waiting', 'retrying', 'queued'].includes(run.status);
  const triggerType = run.trigger_type || run.shared_context?.trigger_source_label || '';
  const creditsUsed = run.credits_used;

  return (
    <button
      onClick={onClick}
      data-testid={`run-card-${run.system_run_id}`}
      className={`w-full text-left bg-[#1E2836] border rounded-xl p-5 transition-all duration-200 group ${
        isActive
          ? 'border-[rgba(59,130,246,0.15)] hover:border-[rgba(59,130,246,0.3)]'
          : 'border-[rgba(255,255,255,0.05)] hover:border-[rgba(255,255,255,0.12)]'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-[13px] text-[#F0F4F8] font-medium truncate" data-testid="run-summary">
            {summary || 'System run'}
          </p>
          {workerNames.length > 0 && (
            <p className="text-[11px] text-[#7D8896] mt-1 truncate">
              {workerNames.join(' → ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 ml-3 flex-shrink-0">
          <StatusBadge status={run.status} />
          <CaretRight size={14} className="text-[#3A4150] group-hover:text-[#7D8896] transition-colors" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <WorkerProgress
            workerChain={chain}
            currentIndex={run.current_worker_index || 0}
            status={run.status}
          />
          {triggerType && (
            <span data-testid={`trigger-badge-${run.system_run_id}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-[rgba(255,255,255,0.04)] text-[#7D8896]">
              {triggerType}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#5C6778]">
          {creditsUsed != null && <span data-testid="run-credits">{Number(creditsUsed).toFixed(1)} cr</span>}
          {run.duration_ms && <span>{formatDuration(run.duration_ms)}</span>}
          <span>{timeAgo(run.created_at)}</span>
        </div>
      </div>
    </button>
  );
}

/* ════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════ */
export default function SystemRuns() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);

  const fetchRuns = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (filter) params.set('status', filter);
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/system-runs?${params}`,
        {},
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        setRuns(data.runs || []);
      }
    } catch (err) {
      console.error('Failed to load system runs:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, filter]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Auto-refresh for active runs
  useEffect(() => {
    const hasActive = runs.some(r => ['running', 'waiting', 'queued', 'retrying'].includes(r.status));
    if (!hasActive) return;
    const interval = setInterval(fetchRuns, 5000);
    return () => clearInterval(interval);
  }, [runs, fetchRuns]);

  const activeCount = runs.filter(r => ['running', 'waiting', 'queued', 'retrying'].includes(r.status)).length;

  return (
    <div className="max-w-[960px] mx-auto" data-testid="system-runs-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/workers')}
            data-testid="back-to-workers-btn"
            className="flex items-center gap-1 text-[#7D8896] hover:text-[#B0B8C4] text-xs transition-colors"
          >
            <ArrowLeft size={14} />
            Workers
          </button>
          <div className="w-px h-4 bg-[rgba(255,255,255,0.06)]" />
          <div>
            <h1 className="text-lg font-semibold text-[#F0F4F8]" data-testid="page-title">Activity</h1>
          </div>
        </div>
        {activeCount > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse" />
            <span className="text-[11px] text-[#7D8896]" data-testid="active-count">
              {activeCount} active
            </span>
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-5 bg-[#1F2937] rounded-lg border border-[rgba(255,255,255,0.04)] p-0.5 w-fit" data-testid="filter-tabs">
        {FILTER_TABS.map(tab => (
          <button
            key={tab.label}
            onClick={() => { setFilter(tab.id); setLoading(true); }}
            data-testid={`filter-${tab.label.toLowerCase()}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filter === tab.id
                ? 'bg-[#161C24] text-[#F0F4F8]'
                : 'text-[#7D8896] hover:text-[#B0B8C4]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="loading-state">
          <ArrowsClockwise size={20} className="text-[#3A4150] animate-spin" />
        </div>
      ) : runs.length === 0 ? (
        <div className="text-center py-20" data-testid="empty-state">
          <Lightning size={32} className="mx-auto text-[#2A2F38] mb-3" />
          <p className="text-sm text-[#7D8896]">
            {filter ? 'No matching runs' : 'No system runs yet'}
          </p>
          <p className="text-xs text-[#5C6778] mt-1">
            Orchestration runs appear here when workers execute together
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2" data-testid="runs-list">
          {runs.map(run => (
            <RunCard
              key={run.system_run_id}
              run={run}
              onClick={() => navigate(`/workers/activity/${run.system_run_id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
